import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { Phone, Mail, MessageCircle, Clock, Users, Brain, TrendingUp, PhoneIncoming, PhoneOutgoing, Activity, Calendar, ChevronDown } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  call: '#3b82f6',
  email: '#8b5cf6',
  whatsapp: '#22c55e',
  sms: '#f59e0b',
  teams: '#6366f1',
  note: '#94a3b8',
  crm_note: '#ec4899',
  meeting: '#14b8a6',
};

const TYPE_LABELS = {
  call: 'Appels',
  email: 'Emails',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  teams: 'Teams',
  note: 'Notes',
  crm_note: 'CRM',
  meeting: 'Réunions',
};

function KpiCard({ icon: Icon, iconColor, label, value, sub, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-heading font-bold leading-none">{value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniBar({ data, maxVal }) {
  if (!maxVal) return null;
  const types = ['call', 'email', 'whatsapp', 'sms', 'teams', 'crm_note'];
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-muted/50 w-full">
      {types.map(t => {
        const count = data[t] || 0;
        if (!count) return null;
        const pct = (count / maxVal) * 100;
        return (
          <div
            key={t}
            style={{ width: `${pct}%`, backgroundColor: TYPE_COLORS[t] }}
            title={`${TYPE_LABELS[t]}: ${count}`}
          />
        );
      })}
    </div>
  );
}

function ActivityChart({ timeline }) {
  const types = ['call', 'email', 'whatsapp', 'sms', 'teams', 'crm_note'];

  // Compute 3-day moving average for trend line
  const chartData = useMemo(() => {
    return timeline.map((day, i) => {
      const dateObj = new Date(day.date + 'T00:00:00');
      const label = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      const shortLabel = dateObj.toLocaleDateString('fr-FR', { day: 'numeric' });

      // 3-day moving average
      const window = 3;
      const start = Math.max(0, i - Math.floor(window / 2));
      const end = Math.min(timeline.length, i + Math.ceil(window / 2) + 1);
      const slice = timeline.slice(start, end);
      const trend = Math.round(slice.reduce((s, d) => s + (d.total || 0), 0) / slice.length);

      return {
        ...day,
        label,
        shortLabel,
        trend,
      };
    });
  }, [timeline]);

  const CustomTooltip = ({ active, payload, label: tooltipLabel }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
      <div className="bg-popover border rounded-md shadow-md p-2.5 text-xs">
        <p className="font-medium mb-1">{data.label}</p>
        <p className="text-muted-foreground mb-1">{data.total} échanges</p>
        {types.map(t => data[t] ? (
          <p key={t} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: TYPE_COLORS[t] }} />
            {TYPE_LABELS[t]}: {data[t]}
          </p>
        ) : null)}
        <p className="text-muted-foreground mt-1 border-t pt-1 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Tendance: {data.trend}
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {types.map(t => (
          <span key={t} className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: TYPE_COLORS[t] }} />
            {TYPE_LABELS[t]}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-dashed border-primary inline-block" />
          Tendance
        </span>
      </div>

      {/* Chart */}
      <div data-testid="activity-chart" style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} vertical={false} />
            <XAxis
              dataKey="shortLabel"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))', opacity: 0.4 }}
              interval={timeline.length > 20 ? 'equidistantPreserveStart' : 0}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }} />
            {types.map(t => (
              <Bar
                key={t}
                dataKey={t}
                stackId="stack"
                fill={TYPE_COLORS[t]}
                radius={t === 'crm_note' ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                maxBarSize={32}
              />
            ))}
            <Line
              type="monotone"
              dataKey="trend"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
              activeDot={{ r: 3, fill: '#fff' }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function OwnerDashboardPage() {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [clones, setClones] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ov, cl, tl] = await Promise.all([
          axios.get(`${API}/dashboard/overview?days=${days}`, { headers }),
          axios.get(`${API}/dashboard/activity-by-clone?days=${days}`, { headers }),
          axios.get(`${API}/dashboard/activity-timeline?days=${days}`, { headers }),
        ]);
        setOverview(ov.data);
        setClones(cl.data.clones || []);
        setTimeline(tl.data.timeline || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, token]);

  if (loading || !overview) {
    return (
      <DashboardLayout title="Activité">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const maxCloneTotal = Math.max(...clones.map(c => c.total), 1);

  return (
    <DashboardLayout title="Activité">
      <div className="space-y-6 max-w-6xl">
        {/* Period selector */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-heading font-semibold" data-testid="activity-title">Vue d'ensemble</h2>
            <p className="text-sm text-muted-foreground">Activité de vos clones sur les {days} derniers jours</p>
          </div>
          <div className="flex gap-1 bg-muted rounded-lg p-0.5" data-testid="period-selector">
            {[7, 14, 30].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  days === d ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`period-${d}`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
          <KpiCard
            icon={Activity}
            iconColor="bg-blue-500/10 text-blue-500"
            label={`Échanges (${days}j)`}
            value={overview.total_memories.toLocaleString()}
            sub={`${overview.today.total} aujourd'hui`}
            testId="kpi-total"
          />
          <KpiCard
            icon={Phone}
            iconColor="bg-green-500/10 text-green-500"
            label="Appels"
            value={overview.calls.total.toLocaleString()}
            sub={`${overview.calls.inbound} reçus · ${overview.calls.outbound} émis`}
            testId="kpi-calls"
          />
          <KpiCard
            icon={Clock}
            iconColor="bg-amber-500/10 text-amber-500"
            label="Temps au téléphone"
            value={overview.calls.duration_label}
            testId="kpi-duration"
          />
          <KpiCard
            icon={Brain}
            iconColor="bg-purple-500/10 text-purple-500"
            label="Clones actifs"
            value={overview.clones}
            sub={`${overview.active_contacts.toLocaleString()} contacts`}
            testId="kpi-clones"
          />
        </div>

        {/* Activity timeline chart */}
        <Card data-testid="timeline-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-base font-heading">Activité quotidienne</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ActivityChart timeline={timeline} />
          </CardContent>
        </Card>

        {/* Per-clone breakdown table */}
        <Card data-testid="clones-activity-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-base font-heading">Activité par clone</CardTitle>
            </div>
            <CardDescription className="text-xs">Période : {days} derniers jours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {/* Header */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_80px_100px_120px] gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                <span>Clone</span>
                <span className="text-right">Appels</span>
                <span className="text-right">Emails</span>
                <span className="text-right">WhatsApp</span>
                <span className="text-right">Durée tel</span>
                <span className="text-right">Total</span>
                <span className="text-right">Dernière activité</span>
              </div>

              {/* Rows */}
              {clones.map(c => {
                const lastAct = c.last_activity ? (() => {
                  const diff = Date.now() - new Date(c.last_activity).getTime();
                  const mins = Math.floor(diff / 60000);
                  if (mins < 60) return `il y a ${mins} min`;
                  const hours = Math.floor(mins / 60);
                  if (hours < 24) return `il y a ${hours}h`;
                  return `il y a ${Math.floor(hours / 24)}j`;
                })() : '—';

                return (
                  <div
                    key={c.person_id}
                    className="grid grid-cols-[1fr_80px_80px_80px_80px_100px_120px] gap-2 px-3 py-2.5 items-center hover:bg-muted/50 rounded-md transition-colors"
                    data-testid={`clone-row-${c.person_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <MiniBar data={c.by_type} maxVal={maxCloneTotal} />
                    </div>
                    <span className="text-sm text-right tabular-nums">{c.by_type.call || 0}</span>
                    <span className="text-sm text-right tabular-nums">{c.by_type.email || 0}</span>
                    <span className="text-sm text-right tabular-nums">{c.by_type.whatsapp || 0}</span>
                    <span className="text-sm text-right tabular-nums">{c.call_duration_label}</span>
                    <span className="text-sm text-right font-medium tabular-nums">{c.total.toLocaleString()}</span>
                    <span className="text-xs text-right text-muted-foreground">{lastAct}</span>
                  </div>
                );
              })}

              {clones.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Aucune activité sur cette période.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
